import { sessionStore } from '../auth/session-store';
import { ApiError, isApiErrorBody } from './api-error';

export const API_BASE = '/api/v1';

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  /** attach the bearer token and attempt refresh-and-retry on 401 (default true) */
  auth?: boolean;
}

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
}

let unauthorizedHandler: () => void = () => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    const redirect = window.location.pathname + window.location.search;
    window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`);
  }
};

/** Called when a request is unauthorised and the refresh token can't rescue it. */
export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function toApiError(res: Response, body: unknown): ApiError {
  if (isApiErrorBody(body)) {
    return new ApiError({
      code: body.error.code,
      message: body.error.message,
      status: res.status,
      details: body.error.details,
      requestId: body.error.requestId,
    });
  }
  return new ApiError({
    code: res.status === 401 ? 'UNAUTHENTICATED' : res.status === 404 ? 'NOT_FOUND' : 'UNKNOWN',
    message: res.statusText || `Request failed with status ${res.status}`,
    status: res.status,
  });
}

async function send(url: string, opts: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    return await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: 'Network request failed',
      status: 0,
      details: cause,
    });
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token. Concurrent callers share one request.
 * Resolves true on success; on failure the session is cleared and false is returned.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await send(buildUrl('/auth/refresh'), { method: 'POST' }, null);
      if (!res.ok) {
        sessionStore.clear();
        return false;
      }
      const body = (await parseBody(res)) as TokenResponse;
      sessionStore.setToken(body.accessToken, body.expiresIn);
      return true;
    } catch {
      sessionStore.clear();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Performs a JSON request against the API. Throws ApiError on failure. */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const useAuth = opts.auth ?? true;
  const url = buildUrl(path, opts.query);
  let res = await send(url, opts, useAuth ? sessionStore.getAccessToken() : null);

  if (res.status === 401 && useAuth) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      unauthorizedHandler();
      throw toApiError(res, await parseBody(res));
    }
    res = await send(url, opts, sessionStore.getAccessToken());
    if (res.status === 401) {
      sessionStore.clear();
      unauthorizedHandler();
    }
  }

  const body = await parseBody(res);
  if (!res.ok) throw toApiError(res, body);
  return body as T;
}

let bootstrapPromise: Promise<boolean> | null = null;

/** Restores the session once per page load via silent refresh. Resolves true if signed in. */
export function ensureSession(): Promise<boolean> {
  const { status } = sessionStore.getState();
  if (status === 'authenticated') return Promise.resolve(true);
  if (status === 'anonymous') return Promise.resolve(false);
  bootstrapPromise ??= refreshSession().finally(() => {
    bootstrapPromise = null;
  });
  return bootstrapPromise;
}
